/**
 * CohortsPage.jsx — Super Admin · DeutschBridge Institute
 *
 * Updated: "Assign Teacher" dropdown on each cohort row.
 * Teacher selection calls PUT /api/cohorts/:id/teacher.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from '../../context/ToastContext';
import { SuperAdminShell } from './SuperAdminDashboard';
import { Button, Badge, Modal, Spinner } from '../../components/shared/UI';
import API from '../../utils/apiClient';

// ── Helpers ───────────────────────────────────────────────────
const DAYS   = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
const LEVELS = ['A1','A2','B1','B2'];
const empty  = { name:'', level:'A1', session:'morning', days:[], start_time:'09:00', end_time:'11:00', start_date:'', max_students:20, meet_link:'', teacher_id:'' };

function autoEnd(start) {
  if (!start) return '';
  const d = new Date(start);
  d.setDate(d.getDate() + 70);
  return d.toISOString().split('T')[0];
}
function statusColor(s) {
  return s === 'open' ? 'bg-green-100 text-green-700'
       : s === 'full' ? 'bg-yellow-100 text-yellow-700'
       : 'bg-gray-100 text-gray-500';
}

// ══════════════════════════════════════════════════════════════
// MANAGE STUDENTS PANEL
// ══════════════════════════════════════════════════════════════
function ManageStudentsPanel({ cohort, onClose }) {
  const { addToast } = useToast();
  const [students, setStudents]     = useState([]);
  const [stuLoading, setStuLoad]    = useState(true);
  const [allStudents, setAll]       = useState([]);
  const [allLoading, setAllLoad]    = useState(true);
  const [search, setSearch]         = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [adding, setAdding]         = useState(false);
  const [removingId, setRemovingId] = useState(null);

  const loadCohortStudents = useCallback(() => {
    setStuLoad(true);
    API.get(`/cohorts/${cohort.id}/students`)
      .then(({ data }) => setStudents(data.students || []))
      .catch(() => addToast('Failed to load students', 'error'))
      .finally(() => setStuLoad(false));
  }, [cohort.id]);

  const loadAllStudents = useCallback(() => {
    setAllLoad(true);
    API.get(`/students/by-level/${cohort.level}`)
      .then(({ data }) => setAll(data.students || []))
      .catch(() => {})
      .finally(() => setAllLoad(false));
  }, [cohort.level]);

  useEffect(() => {
    loadCohortStudents();
    loadAllStudents();
  }, [loadCohortStudents, loadAllStudents]);

  const enrolledIds = new Set(students.map(s => s.student_id));
  const filtered = allStudents.filter(s => {
    if (enrolledIds.has(s.student_id)) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return s.name?.toLowerCase().includes(q) || s.email?.toLowerCase().includes(q);
  });

  const handleAdd = async () => {
    if (!selectedId) { addToast('Please select a student', 'error'); return; }
    setAdding(true);
    try {
      await API.post(`/cohorts/${cohort.id}/admin-add`, { studentId: parseInt(selectedId) });
      addToast('Student added!', 'success');
      setSelectedId(''); setSearch('');
      loadCohortStudents(); loadAllStudents();
    } catch (err) {
      addToast(err.response?.data?.error || err.message || 'Failed', 'error');
    } finally { setAdding(false); }
  };

  const handleRemove = async (studentId, name) => {
    if (!window.confirm(`Remove ${name} from this cohort?`)) return;
    setRemovingId(studentId);
    try {
      await API.delete(`/cohorts/${cohort.id}/students/${studentId}`);
      addToast(`${name} removed.`, 'success');
      setStudents(p => p.filter(s => s.student_id !== studentId));
    } catch (err) {
      addToast(err.response?.data?.error || err.message || 'Failed', 'error');
    } finally { setRemovingId(null); }
  };

  const enrolled  = students.length;
  const max       = cohort.max_students || 20;
  const spotsLeft = Math.max(0, max - enrolled);
  const isFull    = spotsLeft === 0;
  const days      = Array.isArray(cohort.days) ? cohort.days : JSON.parse(cohort.days || '[]');

  return (
    <div className="border-t border-gray-200 bg-gray-50">
      {/* Panel header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-white">
        <div className="flex items-center gap-3">
          <div>
            <h3 className="text-sm font-bold text-de-black">{cohort.name}</h3>
            <p className="text-xs text-gray-500">
              {cohort.level} · {cohort.session} · {days.map(d => d.slice(0,3)).join(', ')} · {cohort.start_time}–{cohort.end_time}
            </p>
          </div>
          <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${isFull ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}`}>
            {enrolled}/{max} students
          </span>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-de-black text-lg font-bold">✕</button>
      </div>

      <div className="p-5 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Current students */}
        <div>
          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Current Students ({enrolled})</h4>
          {stuLoading ? (
            <div className="flex justify-center py-8"><Spinner className="text-brand-500" /></div>
          ) : students.length === 0 ? (
            <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-xl">
              <p className="text-xl mb-1">👥</p>
              <p className="text-xs text-gray-400">No students yet. Add students using the panel on the right.</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>{['ID','Name','Level','Action'].map(h => (
                    <th key={h} className="text-left py-2 px-3 font-bold text-gray-500 uppercase tracking-wide">{h}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {students.map((s, i) => (
                    <tr key={s.student_id} className={`border-b border-gray-100 hover:bg-gray-50 ${i%2?'bg-gray-50/40':''}`}>
                      <td className="py-2.5 px-3 font-mono font-bold text-brand-500 whitespace-nowrap">
                        {s.student_number || `DB-${new Date().getFullYear()}-${String(s.student_id).padStart(4,'0')}`}
                      </td>
                      <td className="py-2.5 px-3">
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 rounded-full bg-brand-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                            {s.name?.charAt(0)}
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-de-black truncate">{s.name}</p>
                            <p className="text-gray-400 truncate text-[11px]">{s.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-2.5 px-3">
                        <span className="px-1.5 py-0.5 bg-brand-50 text-brand-600 rounded-full text-xs font-bold">{s.current_level}</span>
                      </td>
                      <td className="py-2.5 px-3">
                        <button onClick={() => handleRemove(s.student_id, s.name)}
                          disabled={removingId === s.student_id}
                          className="px-2 py-1 bg-brand-50 text-brand-600 border border-brand-200 rounded-lg text-xs font-semibold hover:bg-brand-500 hover:text-white hover:border-brand-500 transition-all disabled:opacity-50">
                          {removingId === s.student_id ? '…' : 'Remove'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Add student */}
        <div>
          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Add Student to Cohort</h4>
          {isFull ? (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-xl text-center">
              <p className="text-sm font-bold text-yellow-700 mb-1">⚠ Cohort is Full</p>
              <p className="text-xs text-yellow-600">Remove a student first to add someone new.</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
              <div>
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>Capacity</span><span className="font-semibold">{enrolled}/{max}</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2">
                  <div className="h-2 rounded-full bg-brand-500 transition-all"
                    style={{ width:`${Math.min(100,(enrolled/max)*100)}%` }} />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-de-black mb-1">Search (Level {cohort.level})</label>
                <input type="text" value={search} onChange={e => { setSearch(e.target.value); setSelectedId(''); }}
                  placeholder="Search by name or email..."
                  className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none" />
              </div>
              <div>
                <label className="block text-xs font-bold text-de-black mb-1">
                  Select student {search && <span className="text-gray-400 font-normal">({filtered.length} matching)</span>}
                </label>
                {allLoading ? (
                  <div className="flex items-center gap-2 py-2"><Spinner size="xs" className="text-brand-500"/><span className="text-xs text-gray-400">Loading…</span></div>
                ) : (
                  <select value={selectedId} onChange={e => setSelectedId(e.target.value)}
                    className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none bg-white"
                    size={Math.min(6, filtered.length + 1)}>
                    <option value="">— Select a student —</option>
                    {filtered.map(s => (
                      <option key={s.student_id} value={s.student_id}>
                        {s.student_number || `DB-${new Date().getFullYear()}-${String(s.student_id).padStart(4,'0')}`}  ·  {s.name}  ·  {s.email}
                      </option>
                    ))}
                  </select>
                )}
                {!allLoading && filtered.length === 0 && (
                  <p className="text-xs text-gray-400 mt-1 italic">{search ? 'No students match.' : `No available Level ${cohort.level} students.`}</p>
                )}
              </div>
              <div className="p-2 bg-blue-50 border border-blue-100 rounded-lg">
                <p className="text-xs text-blue-700">ℹ️ As Super Admin you can add any Level {cohort.level} student — no payment check required.</p>
              </div>
              <Button variant="primary" className="w-full" disabled={!selectedId || adding} loading={adding} onClick={handleAdd}>
                + Add to Cohort
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// MAIN COHORTS PAGE
// ══════════════════════════════════════════════════════════════
export default function SuperAdminCohortsPage() {
  const { addToast } = useToast();
  const [cohorts, setCohorts]       = useState([]);
  const [teachers, setTeachers]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [form, setForm]             = useState(empty);
  const [showForm, setShowForm]     = useState(false);
  const [editId, setEditId]         = useState(null);
  const [saving, setSaving]         = useState(false);
  const [deleteModal, setDelete]    = useState(null);
  const [deleting, setDeleting]     = useState(false);
  const [expandedId, setExpanded]   = useState(null);
  const [assigningTeacher, setAssigning] = useState(null); // cohortId being updated

  const load = () => {
    setLoading(true);
    Promise.all([
      API.get('/cohorts').then(r => r.data.cohorts || []),
      API.get('/super-admin/teachers').then(r => r.data.teachers || []),
    ])
      .then(([c, t]) => { setCohorts(c); setTeachers(t); })
      .catch(() => addToast('Failed to load', 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const toggleDay = d => setF('days', form.days.includes(d) ? form.days.filter(x => x !== d) : [...form.days, d]);

  const handleSave = async () => {
    if (!form.name || !form.start_date || form.days.length === 0) {
      addToast('Name, start date, and at least one day are required', 'error'); return;
    }
    setSaving(true);
    try {
      const payload = { ...form, end_date: autoEnd(form.start_date), teacher_id: form.teacher_id || null };
      if (editId) {
        await API.put(`/cohorts/${editId}`, payload);
        addToast('Cohort updated!', 'success');
      } else {
        await API.post('/cohorts', payload);
        addToast('Cohort created!', 'success');
      }
      setShowForm(false); setForm(empty); setEditId(null); load();
    } catch (err) { addToast(err.message || 'Failed', 'error'); }
    finally { setSaving(false); }
  };

  const openEdit = c => {
    setForm({ ...c, days: Array.isArray(c.days) ? c.days : JSON.parse(c.days || '[]'), teacher_id: c.teacher_id || '' });
    setEditId(c.id); setShowForm(true); setExpanded(null);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await API.delete(`/cohorts/${deleteModal.id}`);
      addToast('Deleted', 'success');
      setCohorts(p => p.filter(c => c.id !== deleteModal.id));
      if (expandedId === deleteModal.id) setExpanded(null);
      setDelete(null);
    } catch (err) { addToast(err.message || 'Failed', 'error'); }
    finally { setDeleting(false); }
  };

  const handleAssignTeacher = async (cohortId, teacherId) => {
    setAssigning(cohortId);
    try {
      await API.put(`/cohorts/${cohortId}/teacher`, { teacherId: teacherId || null });
      addToast('Teacher assigned!', 'success');
      // Update local state so the dropdown reflects immediately
      setCohorts(p => p.map(c => c.id === cohortId ? { ...c, teacher_id: teacherId || null } : c));
    } catch (err) { addToast(err.message || 'Failed to assign teacher', 'error'); }
    finally { setAssigning(null); }
  };

  const toggleExpand = id => setExpanded(prev => prev === id ? null : id);
  const handlePanelClose = () => { setExpanded(null); load(); };

  return (
    <SuperAdminShell
      title="Cohorts"
      subtitle="Manage cohorts, assign teachers, and manage student enrollment"
      actions={
        <Button variant="primary" size="sm"
          onClick={() => { setForm(empty); setEditId(null); setShowForm(true); setExpanded(null); }}>
          + Create Cohort
        </Button>
      }
    >
      {/* Create / Edit form */}
      {showForm && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6 shadow-sm">
          <h3 className="text-sm font-bold text-de-black mb-4">
            {editId ? `Editing: ${form.name || 'Cohort'}` : 'Create New Cohort'}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
              <label className="block text-xs font-bold mb-1">Cohort Name *</label>
              <input value={form.name} onChange={e => setF('name', e.target.value)}
                placeholder="e.g. A1 Morning Batch — January 2026"
                className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none" />
            </div>
            <div>
              <label className="block text-xs font-bold mb-1">Level</label>
              <select value={form.level} onChange={e => setF('level', e.target.value)}
                className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none bg-white">
                {LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold mb-1">Session</label>
              <select value={form.session} onChange={e => setF('session', e.target.value)}
                className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none bg-white">
                <option value="morning">Morning</option>
                <option value="afternoon">Afternoon</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold mb-1">Start Time</label>
              <input type="time" value={form.start_time} onChange={e => setF('start_time', e.target.value)}
                className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none" />
            </div>
            <div>
              <label className="block text-xs font-bold mb-1">End Time</label>
              <input type="time" value={form.end_time} onChange={e => setF('end_time', e.target.value)}
                className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none" />
            </div>
            <div>
              <label className="block text-xs font-bold mb-1">Start Date *</label>
              <input type="date" value={form.start_date} onChange={e => setF('start_date', e.target.value)}
                className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none" />
              {form.start_date && <p className="text-xs text-gray-400 mt-1">End: {autoEnd(form.start_date)} (10 weeks)</p>}
            </div>
            <div>
              <label className="block text-xs font-bold mb-1">Max Students</label>
              <input type="number" min="1" max="50" value={form.max_students}
                onChange={e => setF('max_students', parseInt(e.target.value))}
                className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none" />
            </div>
            <div>
              <label className="block text-xs font-bold mb-1">Assign Teacher</label>
              <select value={form.teacher_id || ''} onChange={e => setF('teacher_id', e.target.value || null)}
                className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none bg-white">
                <option value="">— No teacher assigned —</option>
                {teachers.filter(t => t.is_active).map(t => (
                  <option key={t.id} value={t.id}>{t.name} ({(t.assigned_levels || []).join(', ')})</option>
                ))}
              </select>
            </div>
            <div className="lg:col-span-2">
              <label className="block text-xs font-bold mb-1">Google Meet Link</label>
              <input type="url" value={form.meet_link} onChange={e => setF('meet_link', e.target.value)}
                placeholder="https://meet.google.com/abc-defg-hij"
                className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none" />
            </div>
            <div className="lg:col-span-3">
              <label className="block text-xs font-bold mb-2">Class Days *</label>
              <div className="flex gap-2 flex-wrap">
                {DAYS.map(d => (
                  <button key={d} type="button" onClick={() => toggleDay(d)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border-2 transition-all
                      ${form.days.includes(d) ? 'border-brand-500 bg-brand-50 text-brand-600' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}>
                    {d.slice(0, 3)}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="flex gap-2 justify-end mt-4">
            <Button variant="secondary" size="sm" onClick={() => { setShowForm(false); setEditId(null); }}>Cancel</Button>
            <Button variant="primary" size="sm" loading={saving} onClick={handleSave}>
              {editId ? 'Save Changes' : 'Create Cohort'}
            </Button>
          </div>
        </div>
      )}

      {/* Cohorts table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-sm font-bold text-de-black">All Cohorts ({cohorts.length})</h2>
            <p className="text-xs text-gray-400">Click a row to manage students · Use the teacher dropdown to assign teachers</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-10"><Spinner className="text-brand-500" /></div>
        ) : cohorts.length === 0 ? (
          <div className="text-center py-10 text-gray-400 text-sm">
            <p className="text-3xl mb-2">🏫</p>
            No cohorts yet — click "+ Create Cohort" to get started.
          </div>
        ) : (
          cohorts.map((c, i) => {
            const days     = Array.isArray(c.days) ? c.days : JSON.parse(c.days || '[]');
            const enrolled = c.enrolled_count || c.current_students || 0;
            const isOpen   = expandedId === c.id;

            return (
              <div key={c.id} className={`border-b border-gray-200 last:border-0 ${i % 2 ? 'bg-gray-50/30' : 'bg-white'}`}>

                {/* Row */}
                <div className={`flex items-center gap-3 px-5 py-3.5 transition-colors
                  ${isOpen ? 'bg-blue-50/60 border-l-2 border-brand-500' : 'hover:bg-gray-50'}`}>

                  {/* Expand toggle */}
                  <button onClick={() => { setShowForm(false); toggleExpand(c.id); }}
                    className="flex items-center gap-3 flex-1 min-w-0 text-left">
                    <span className={`text-xs text-gray-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-90' : ''}`}>▶</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-bold text-de-black">{c.name}</p>
                        <span className="px-2 py-0.5 bg-brand-50 text-brand-600 rounded-full text-xs font-bold">{c.level}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-bold capitalize ${statusColor(c.status)}`}>{c.status}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {c.session} · {days.map(d => d.slice(0,3)).join(', ')} · {c.start_time}–{c.end_time}
                        {c.start_date && ` · from ${c.start_date}`}
                        {c.teacher_name && <span className="ml-2 text-green-700 font-medium">· 👤 {c.teacher_name}</span>}
                      </p>
                    </div>
                  </button>

                  {/* Enrollment bar */}
                  <div className="hidden sm:flex items-center gap-2 flex-shrink-0 w-36">
                    <div className="flex-1 bg-gray-100 rounded-full h-2">
                      <div className="h-2 rounded-full bg-brand-500 transition-all"
                        style={{ width:`${Math.min(100,(enrolled/c.max_students)*100)}%` }} />
                    </div>
                    <span className="text-xs text-gray-600 whitespace-nowrap">{enrolled}/{c.max_students}</span>
                  </div>

                  {/* Assign Teacher dropdown */}
                  <div className="flex-shrink-0 w-44" onClick={e => e.stopPropagation()}>
                    <select
                      value={c.teacher_id || ''}
                      onChange={e => handleAssignTeacher(c.id, e.target.value ? parseInt(e.target.value) : null)}
                      disabled={assigningTeacher === c.id}
                      className="w-full px-2 py-1.5 text-xs border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none bg-white disabled:opacity-50">
                      <option value="">Assign teacher…</option>
                      {teachers.filter(t => t.is_active).map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Edit / Delete */}
                  <div className="flex gap-1.5 flex-shrink-0" onClick={e => e.stopPropagation()}>
                    <Button size="sm" variant="secondary" onClick={() => openEdit(c)}>Edit</Button>
                    <Button size="sm" variant="danger"    onClick={() => setDelete(c)}>Del</Button>
                  </div>
                </div>

                {/* Expanded students panel */}
                {isOpen && <ManageStudentsPanel cohort={c} onClose={handlePanelClose} />}
              </div>
            );
          })
        )}
      </div>

      {/* Delete confirm */}
      <Modal isOpen={!!deleteModal} onClose={() => setDelete(null)} title="Delete Cohort" size="sm">
        {deleteModal && (
          <div>
            <p className="text-sm text-de-text mb-2">Delete <strong>"{deleteModal.name}"</strong>?</p>
            <p className="text-xs text-brand-500 mb-4">⚠ This removes all student enrollments and timetable entries.</p>
            <div className="flex gap-2 justify-end">
              <Button variant="secondary" size="sm" onClick={() => setDelete(null)}>Cancel</Button>
              <Button variant="danger"    size="sm" loading={deleting} onClick={handleDelete}>Delete</Button>
            </div>
          </div>
        )}
      </Modal>
    </SuperAdminShell>
  );
}
